import { useLocation } from 'react-router-dom';
import { useTripVisitStats } from '../../hooks/trip/tripVisitStats';
import { useTripData } from '../../hooks/trip/useTripData';
import { useTripInteractions } from '../../hooks/trip/useTripInteractions';
import { useTripOwnerProfile } from '../../hooks/trip/useTripOwnerProfile';
import { useScrollToAnchor } from '../../hooks/trip/useScrollToAnchor';
import { useTripSortOrder } from '../../hooks/trip/useTripSortOrder';
import { useAuthenticatedUser } from '../../hooks/useAuthenticatedUser';
import NotFoundPage from '../notFound/NotFoundPage';
import { TripHeader } from './TripHeader';
import { TripActions } from './TripActions';
import { SortToggleButton } from './SortToggleButton';
import { TripEndMarker } from './TripEndMarker';
import { StoryList } from './StoryList';
import { StoryListSkeleton, TripHeaderSkeleton } from '../../components/skeletons';

export const TripPage = () => {
  const location = useLocation();

  const { profile: user } = useAuthenticatedUser();

  const { data, error, linkSecret } = useTripData(location.pathname);
  const { interactions } = useTripInteractions(data?.trip?.id, linkSecret, user?.userId);
  const { ownerProfile, isOwner } = useTripOwnerProfile(data?.trip?.ownerId, user?.userId);
  const { currentSortOrder, toggleSortOrder } = useTripSortOrder(
    data?.trip?.id,
    data?.trip?.sortOrder || 'DESC',
  );

  useTripVisitStats(data, linkSecret);
  useScrollToAnchor(data?.stories);

  if (error) {
    return <NotFoundPage />;
  }

  return (
    <div className="bg-canvas min-h-screen">
      {/* Trip Header - show skeleton if not loaded */}
      {!data?.trip ? (
        <TripHeaderSkeleton />
      ) : (
        <TripHeader trip={data.trip} ownerProfile={ownerProfile} />
      )}

      {/* Trip Actions - only show when the trip is loaded */}
      {data?.trip && (
        <TripActions isOwner={isOwner} tripId={data.trip.id} linkSecret={linkSecret} />
      )}

      {/* Sort Toggle - left-aligned above stories */}
      {data?.trip && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 mb-4">
          <div className="flex justify-start">
            <SortToggleButton currentSortOrder={currentSortOrder} onToggle={toggleSortOrder} />
          </div>
        </div>
      )}

      {/* Trip End Marker - appears before stories if DESC (newest first) */}
      {data?.trip && currentSortOrder === 'DESC' && new Date(data.trip.endDate) < new Date() && (
        <div className="bg-canvas">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <TripEndMarker endDate={data.trip.endDate} sortOrder={currentSortOrder} />
          </div>
        </div>
      )}

      {/* Story List - show skeleton if not loaded */}
      {!data?.stories ? (
        <StoryListSkeleton />
      ) : (
        <StoryList
          stories={data.stories}
          linkSecret={linkSecret}
          interactions={interactions}
          isOwner={isOwner}
          sortOrder={currentSortOrder}
          interactionsEnabled={!!data.trip.interactionsEnabled}
        />
      )}

      {/* Trip End Marker - appears after stories if ASC (oldest first) */}
      {data?.trip &&
        data?.stories &&
        currentSortOrder === 'ASC' &&
        new Date(data.trip.endDate) < new Date() && (
          <div className={`${(data.stories.length - 1) % 2 === 0 ? 'bg-canvas' : 'bg-surface'}`}>
            <div className="max-w-7xl mx-auto px-4 md:px-8">
              <TripEndMarker endDate={data.trip.endDate} sortOrder={currentSortOrder} />
            </div>
          </div>
        )}
    </div>
  );
};
